/* Legends free-gift promo — pure sync logic, no DOM and no network.
   Loaded before legends-cart-drawer.js, which performs the operations.

   The promo has been through three shapes. Band-matched hard cases until 2026-08-26; a
   single Lens Cleaner Kit for every frame until 2026-09-22; and, from 2026-09-22, band-
   matched hard cases again but on a much narrower rule:

     * only a line bought on a pre-order selling plan earns anything at all, UNLESS its band
       is one of `alwaysGiftBands`. That is decided on the LINE, not on the product: the
       same frame can be in stock in one colourway and on pre-order in another, and
       /cart.js tells us which by whether the line carries a selling_plan_allocation. The
       exception is AC/DC, whose frames are in stock and whose case James made an automatic
       gift on every pair on 2026-09-22, so there is nothing for the customer to remember
       and no code to type;
     * the gift is the hard case for the frame's own band, so a mixed-band cart gets one
       gift line per band rather than one shared line.

   The file name, the asset name and the `_free_case` line property all keep their old names
   on purpose: renaming a theme asset means a push-plus-delete against a live store, and the
   property name is what tells the planner that a gift line left over from an earlier shape
   of the promo is a gift line at all. Read "case" as "gift" throughout.

   Nothing here names a band, a product, a variant or a selling plan. The whole map is
   rendered from the catalogue by snippets/legends-cart-drawer.liquid, so swapping a band's
   gift is a tagging job in the Shopify admin and not a code change. */
(function (window) {
  'use strict';

  var GIFT_PROP = '_free_case';
  /* Written onto every gift line this planner adds. The value is never read for meaning —
     staleness is decided on the variant, not on this — so it stays a constant rather than
     the band tag it used to be. It must simply be non-blank: cart-item.liquid gates the
     locked-line rendering on `properties._free_case != blank`. */
  var GIFT_MARK = '1';
  /* legends-ship-together.js's own marker. A line carrying it had NO selling plan of its
     own: the customer bought it in stock and the re-dating pass put it on a pre-order date
     so the whole basket ships in one parcel. Read here, never written. */
  var TOGETHER_PROP = '_ships_together';

  function hasOwn(o, k) { return Object.prototype.hasOwnProperty.call(o, k); }

  /* The variant a cart line holds. /cart.js gives both `variant_id` and `id` (they are
     the same value); neither is guaranteed on a hand-built line, hence the null. */
  function lineVariantId(item) {
    if (item.variant_id != null) return item.variant_id;
    if (item.id != null) return item.id;
    return null;
  }

  /* The selling plan a cart line was bought on, or null. /cart.js nests it under
     selling_plan_allocation; a hand-built line may not carry it at all.

     Since 2026-09-22 this is also the qualifying test: null means the customer bought the
     frame in stock, and an in-stock frame earns no gift. */
  function linePlanId(item) {
    var alloc = item.selling_plan_allocation;
    if (!alloc || !alloc.selling_plan) return null;
    var id = alloc.selling_plan.id;
    return id == null ? null : id;
  }

  /* The product a cart line belongs to. Frames are recognised by this rather than by
     handle because Shopify TRANSLATES handles per locale: on /de/ the same frame is
     `iron-maiden-50-jahre-sonnenbrille`, on /es/ `...-lentes-de-sol`. The Liquid-rendered
     map takes the locale of the page that rendered it while /cart.js returns the default
     locale's handles, so the two silently disagree and no frame ever matches. Product ids
     do not translate. /cart.js always carries product_id; a hand-built line may not. */
  function lineProductId(item) {
    if (item.product_id != null) return item.product_id;
    return null;
  }

  /* The gift twin a frame of a given band, bought on a given plan, should receive.

     `config.giftsByBand` maps a band tag to the twins carrying that band tag, each with the
     selling plan ids attached to it. Normally a band has exactly one twin and the plan list
     is only a safety net. It is a LIST because of the 2026-09-16 lesson: STOQ runs one offer
     per ship date, a variant can only be added on a plan attached to it, and STOQ treats a
     variant sitting in two offers as belonging to the first. A second ship date therefore
     needs a second twin per band, and a November frame must get the November twin.

     A band with no twin returns null, and the caller adds nothing for that frame rather
     than guessing at another band's case. */
  /* True when a band's frames earn their case whether they are pre-ordered or in stock.
     Rendered from a theme setting, so turning AC/DC's automatic case off, or giving another
     band one, is an admin job. A band not listed keeps the pre-order-only rule. */
  function alwaysGifts(band, config) {
    if (!band) return false;
    var always = config.alwaysGiftBands || {};
    return hasOwn(always, band) && !!always[band];
  }

  function giftForBand(band, planId, config) {
    if (!band) return null;
    var byBand = config.giftsByBand || {};
    if (!hasOwn(byBand, band)) return null;
    var twins = byBand[band] || [];
    var i, k, t;
    if (planId != null) {
      for (i = 0; i < twins.length; i++) {
        t = twins[i];
        if (!t || !t.variantId || !t.planIds) continue;
        for (k = 0; k < t.planIds.length; k++) {
          if (String(t.planIds[k]) === String(planId)) return t;
        }
      }
    }
    // No twin claims this plan: fall back to the band's first usable twin rather than
    // silently withholding a gift the page has already promised.
    for (i = 0; i < twins.length; i++) {
      if (twins[i] && twins[i].variantId) return twins[i];
    }
    return null;
  }

  /* True when any band has a usable gift twin configured. When nothing does, the planner
     adds nothing AND touches nothing: with no twin to compare a line against, every
     existing gift line would otherwise look stale and every cart on the store would be
     stripped on the next drawer open. */
  function anyGiftConfigured(config) {
    var byBand = config.giftsByBand || {};
    var band, twins, i;
    for (band in byBand) {
      if (!hasOwn(byBand, band)) continue;
      twins = byBand[band] || [];
      for (i = 0; i < twins.length; i++) {
        if (twins[i] && twins[i].variantId) return true;
      }
    }
    return false;
  }

  /* Returns the operations needed to make the cart's gift lines match its frames.
     Pure: performs no I/O and mutates nothing. Returns [] when already correct.

     One gift per qualifying frame; one gift LINE per twin, so a band's frames share a line
     whose quantity is the number of that band's pre-ordered frames, and a mixed-band cart
     gets one line per band. Lines are keyed on the TWIN VARIANT rather than on the band so
     that two bands pointing at the same twin — a misconfiguration, but a cheap one to
     survive — merge into one line instead of fighting over it. */
  function computeCaseSyncOps(cart, config) {
    if (!cart || !cart.items || !config || !config.enabled) return [];
    var frameBands = config.frameBandByProductId || {};
    var anyGift = anyGiftConfigured(config);
    var i, it;

    var desired = [];     // [{ variantId, planId, quantity }] in first-seen order
    var byVariant = {};   // variantId -> entry in desired
    var current = {};     // variantId -> the existing, correct gift line
    var unread = [];      // gift lines whose variant cannot be read
    var stale = [];       // gift lines that are not (or no longer) a wanted gift
    var giftLines = [];

    for (i = 0; i < cart.items.length; i++) {
      it = cart.items[i];
      if (!it) continue;

      var props = it.properties || {};
      if (props[GIFT_PROP]) {
        giftLines.push(it);
        continue;
      }

      // Only products in the frames collection count, so the gift itself — and any case
      // or kit the customer actually paid for — can never count itself as a frame. The
      // map also leaves out the frames James has excluded from the promo, so an excluded
      // product falls out here with no separate test.
      var pid = lineProductId(it);
      if (pid == null || !hasOwn(frameBands, pid)) continue;
      var band = frameBands[pid];
      if (!band) continue;

      // The pre-order test. A frame bought in stock earns nothing, unless its band gives
      // its case away on every pair.
      //
      // The marker is what stops this being a lie. legends-ship-together.js runs FIRST and
      // moves in-stock lines onto the pre-order date so the basket goes out as one parcel,
      // which hands them a selling plan; by the time this planner sees them they look
      // pre-ordered. It marks every line it moves, so a marked line is read here as what
      // the customer actually did, which was buy it in stock. Without this, putting one
      // November frame in the basket would start handing out cases with every in-stock pair
      // in it, whatever the band.
      var planId = linePlanId(it);
      var boughtInStock = planId == null || !!props[TOGETHER_PROP];
      if (boughtInStock && !alwaysGifts(band, config)) continue;

      var g = giftForBand(band, planId, config);
      if (!g || !g.variantId) continue;   // no twin for this band: add nothing
      var entry = byVariant[g.variantId];
      if (!entry) {
        entry = { variantId: g.variantId, planId: null, quantity: 0 };
        byVariant[g.variantId] = entry;
        desired.push(entry);
      }
      entry.quantity += it.quantity;
      // Mirror the frame's pre-order plan onto the gift so both land in the same
      // delivery group. Without it Shopify sees "ships now" against the frame's dated
      // group and bills postage twice (UK £7.98 vs £3.99, measured at real checkout
      // 2026-08-11). First frame that HAS a plan wins: an always-gift frame bought in
      // stock carries none and must not veto a later frame of the same band that does.
      // legends-ship-together.js afterwards carries every line, gift included, onto the
      // latest date in the cart, so a gift added with no plan still ends up in one group.
      if (entry.planId == null && planId != null) entry.planId = planId;
    }

    // No gift configured at all: leave existing gift lines alone rather than guess.
    if (!anyGift) return [];

    // Sort the existing gift lines. A line holding a wanted twin is the current line for
    // that twin (a second such line is a duplicate: the later wins and the earlier is
    // removed — keeping both doubles the giveaway). Anything else under the gift property
    // — a retired hard case, the Lens Cleaner Kit twin from the 2026-08-26 promo, the
    // purchasable case, a band whose frames have all gone or are all in stock now — is
    // stale and removed. A line whose variant cannot be read is never deleted on a guess;
    // it stands in for the first wanted twin, as before.
    var j, line, vid;
    for (j = 0; j < giftLines.length; j++) {
      line = giftLines[j];
      vid = lineVariantId(line);
      if (vid == null) { unread.push(line); continue; }
      if (byVariant[vid]) {
        if (current[vid]) stale.push(current[vid]);
        current[vid] = line;
      } else {
        stale.push(line);
      }
    }
    for (j = 0; j < unread.length; j++) {
      if (desired.length && !current[desired[0].variantId]) {
        current[desired[0].variantId] = unread[j];
      } else if (desired.length) {
        stale.push(unread[j]);
      }
      // No frames left: an unreadable gift line is left alone, not deleted blind.
    }

    var ops = [];

    // Stale gift lines go first. Removing before adding means the cart never briefly holds
    // two lines both claiming to be the gift, and if the replacement add then fails the
    // customer is left with no gift rather than charged for one the UI called free. A stale
    // line in a cart with no frames left is simply removed: nothing asks for a replacement.
    for (j = 0; j < stale.length; j++) {
      ops.push({ type: 'change', key: stale[j].key, quantity: 0 });
    }

    for (j = 0; j < desired.length; j++) {
      var want = desired[j];
      var have = current[want.variantId];
      if (have) {
        if (have.quantity !== want.quantity) {
          ops.push({ type: 'change', key: have.key, quantity: want.quantity });
        }
      } else {
        var addOp = { type: 'add', variantId: want.variantId, quantity: want.quantity };
        // Only set when a frame of that band actually carried a plan, so an always-gift
        // band bought in stock produces a plain add and the gift ships when the frame does.
        if (want.planId != null) addOp.sellingPlanId = want.planId;
        ops.push(addOp);
      }
    }

    return ops;
  }

  window.LegendsFreeCase = {
    GIFT_PROP: GIFT_PROP,
    GIFT_MARK: GIFT_MARK,
    alwaysGifts: alwaysGifts,
    giftForBand: giftForBand,
    computeCaseSyncOps: computeCaseSyncOps
  };
})(typeof window !== 'undefined' ? window : this);
